import { Pill, Table } from "../components";

/**
 * Application traffic between two agents (internal/traffic): a mix of flows,
 * each one application measured in its own terms. The editor builds the
 * profile a "traffic" two-ended test carries; the result view reads what it
 * found.
 */

export type TrafficApp = "voip" | "video" | "interactive" | "dns" | "web" | "stream" | "bulk";

export interface TrafficFlow {
  id: string;
  app: TrafficApp;
  count: number;
  direction?: "up" | "down" | "both";
  dscp?: number;
  rate_kbps?: number;
}

export interface TrafficProfile {
  flows: TrafficFlow[];
}

/** What each application is, how it is measured, and its defaults. */
export const APPS: Record<TrafficApp, { label: string; measure: string; dscp: number; rate?: number; directional: boolean; rateLabel?: string }> = {
  voip: { label: "VoIP calls", measure: "MOS", dscp: 46, directional: true },
  video: { label: "Video calls", measure: "frames lost and late", dscp: 34, rate: 2000, directional: true, rateLabel: "bitrate" },
  interactive: { label: "Remote desktop / SSH", measure: "response time", dscp: 18, directional: false },
  dns: { label: "DNS lookups", measure: "response time, failures", dscp: 16, directional: false },
  web: { label: "Web browsing", measure: "page load time", dscp: 0, directional: false },
  stream: { label: "Video streaming", measure: "stalls", dscp: 26, rate: 5000, directional: true, rateLabel: "bitrate" },
  bulk: { label: "Bulk transfer", measure: "goodput", dscp: 8, directional: true, rateLabel: "cap" },
};

/** Common mixes to start from; mirrors internal/traffic.Presets. */
export const TRAFFIC_PRESETS: { name: string; description: string; profile: TrafficProfile }[] = [
  { name: "Branch office", description: "Calls, a video meeting, browsing, lookups and a background backup", profile: { flows: [
    { id: "calls", app: "voip", count: 5 },
    { id: "meeting", app: "video", count: 1 },
    { id: "browsing", app: "web", count: 5 },
    { id: "lookups", app: "dns", count: 2 },
    { id: "backup", app: "bulk", count: 1, rate_kbps: 20000 },
  ] } },
  { name: "Contact centre", description: "Many concurrent calls with desktop sessions", profile: { flows: [
    { id: "calls", app: "voip", count: 30 },
    { id: "desktops", app: "interactive", count: 30 },
    { id: "crm", app: "web", count: 10 },
  ] } },
  { name: "Video heavy", description: "Meetings and streaming competing for the link", profile: { flows: [
    { id: "meetings", app: "video", count: 4 },
    { id: "streams", app: "stream", count: 3 },
    { id: "calls", app: "voip", count: 2 },
  ] } },
  { name: "Voice quality under load", description: "Calls sharing the link with a transfer that fills it", profile: { flows: [
    { id: "calls", app: "voip", count: 10 },
    { id: "transfer", app: "bulk", count: 2 },
  ] } },
];

const PER_INSTANCE_KBPS: Partial<Record<TrafficApp, number>> = { voip: 88, interactive: 40, dns: 8, web: 4000 };

/** The most the mix offers in one direction, kbit/s; null when a bulk flow has no cap. */
export function offeredKbps(p: TrafficProfile): number | null {
  let total = 0;
  for (const f of p.flows) {
    const rate = f.rate_kbps || APPS[f.app].rate;
    if (f.app === "bulk" && !f.rate_kbps) return null;
    total += (PER_INSTANCE_KBPS[f.app] ?? rate ?? 0) * (f.count || 1);
  }
  return total;
}

export function kbps(v: number): string {
  return v >= 1000 ? `${(v / 1000).toFixed(v >= 10_000 ? 0 : 1)} Mbit/s` : `${Math.round(v)} kbit/s`;
}

/** A saved mix, as the library lists it. */
export interface SavedMix { id: string; name: string; description: string; profile: { flows: unknown[] } }

/** Build or edit a mix: start from a saved mix or a preset, then change any flow. */
export function TrafficProfileEditor({ value, onChange, saved = [] }: {
  value: TrafficProfile;
  onChange: (p: TrafficProfile) => void;
  /** The tenant's saved mixes, offered first. */
  saved?: SavedMix[];
}) {
  const flows = value.flows;
  const set = (i: number, change: Partial<TrafficFlow>) =>
    onChange({ flows: flows.map((f, n) => (n === i ? { ...f, ...change } : f)) });
  const add = () => {
    const app: TrafficApp = "voip";
    let id: string = app;
    for (let n = 2; flows.some((f) => f.id === id); n++) id = `${app}-${n}`;
    onChange({ flows: [...flows, { id, app, count: 1 }] });
  };
  const offered = offeredKbps(value);

  return (
    <div className="traffic-editor">
      <label htmlFor="traffic-preset">Start from</label>
      <select id="traffic-preset" value="" onChange={(e) => {
        const v = e.target.value;
        const mine = saved.find((x) => `saved:${x.id}` === v);
        if (mine) {
          onChange({ flows: (mine.profile.flows as TrafficFlow[]).map((f) => ({ ...f })) });
          return;
        }
        const p = TRAFFIC_PRESETS.find((x) => `preset:${x.name}` === v);
        if (p) onChange({ flows: p.profile.flows.map((f) => ({ ...f })) });
      }}>
        <option value="">choose a mix…</option>
        {saved.length ? (
          <optgroup label="Saved">
            {saved.map((p) => <option key={p.id} value={`saved:${p.id}`}>{p.name}{p.description ? ` — ${p.description}` : ""}</option>)}
          </optgroup>
        ) : null}
        <optgroup label="Presets">
          {TRAFFIC_PRESETS.map((p) => <option key={p.name} value={`preset:${p.name}`}>{p.name} — {p.description}</option>)}
        </optgroup>
      </select>

      <Table head={<><th>Name</th><th>Application</th><th className="num">How many</th><th>Direction</th><th>Rate (kbit/s)</th><th className="num">DSCP</th><th /></>}>
        {flows.map((f, i) => {
          const a = APPS[f.app];
          return (
            <tr key={i}>
              <td><input aria-label={`Flow ${i + 1} name`} value={f.id} onChange={(e) => set(i, { id: e.target.value })} style={{ width: 110 }} /></td>
              <td>
                <select aria-label={`Flow ${i + 1} application`} value={f.app} onChange={(e) => set(i, { app: e.target.value as TrafficApp, rate_kbps: undefined, direction: undefined, dscp: undefined })}>
                  {(Object.keys(APPS) as TrafficApp[]).map((k) => <option key={k} value={k}>{APPS[k].label}</option>)}
                </select>
                <div className="muted" style={{ fontSize: 11.5 }}>measured as {a.measure}</div>
              </td>
              <td className="num">
                <input aria-label={`Flow ${i + 1} count`} type="number" min={1} max={200} value={f.count}
                  onChange={(e) => set(i, { count: Math.max(1, Math.min(200, Number(e.target.value) || 1)) })} style={{ width: 64 }} />
              </td>
              <td>
                {a.directional ? (
                  <select aria-label={`Flow ${i + 1} direction`} value={f.direction ?? (f.app === "stream" || f.app === "bulk" ? "down" : "both")}
                    onChange={(e) => set(i, { direction: e.target.value as TrafficFlow["direction"] })}>
                    <option value="both">both ways</option>
                    <option value="up">client → responder</option>
                    <option value="down">responder → client</option>
                  </select>
                ) : <span className="muted">both ways</span>}
              </td>
              <td>
                {a.rateLabel ? (
                  <input aria-label={`Flow ${i + 1} rate`} type="number" min={0} value={f.rate_kbps ?? ""}
                    placeholder={a.rate ? String(a.rate) : "no cap"}
                    onChange={(e) => set(i, { rate_kbps: e.target.value === "" ? undefined : Math.max(0, Number(e.target.value)) })}
                    style={{ width: 90 }} />
                ) : <span className="muted">fixed</span>}
              </td>
              <td className="num">
                <input aria-label={`Flow ${i + 1} DSCP`} type="number" min={0} max={63} value={f.dscp ?? ""} placeholder={String(a.dscp)}
                  onChange={(e) => set(i, { dscp: e.target.value === "" ? undefined : Math.max(0, Math.min(63, Number(e.target.value))) })}
                  style={{ width: 56 }} />
              </td>
              <td style={{ textAlign: "right" }}>
                <button type="button" className="danger" disabled={flows.length === 1}
                  onClick={() => onChange({ flows: flows.filter((_, n) => n !== i) })} aria-label={`Remove flow ${i + 1}`}>Remove</button>
              </td>
            </tr>
          );
        })}
      </Table>
      <div className="row" style={{ marginTop: 8, justifyContent: "space-between" }}>
        <button type="button" onClick={add} disabled={flows.length >= 32}>Add flow</button>
        <span className="muted" style={{ fontSize: 12.5 }}>
          {offered == null ? "Offers whatever the path will take: a bulk flow has no cap." : `Offers up to ${kbps(offered)} each way.`}
        </span>
      </div>
    </div>
  );
}

interface DirStats {
  sent: number; received: number; loss_pct: number; jitter_ms: number; reordered: number;
  dscp_seen?: number; dscp_kept?: boolean;
}

export interface TrafficFlowResult {
  id: string; app: TrafficApp; count: number; dscp: number; direction: string;
  up?: DirStats; down?: DirStats;
  mos?: number; min_mos?: number; rtt_ms?: number; p95_ms?: number; requests?: number; answered?: number;
  frames_sent?: number; frames_lost_pct?: number; frames_late_pct?: number;
  transactions?: number; failed?: number; median_ms?: number;
  stalls?: number; stalled_ms?: number; goodput_kbps?: number; error?: string;
}

export interface TrafficResultData {
  duration_s: number;
  flows: TrafficFlowResult[];
  summary?: string;
}

type Tone = "good" | "warn" | "crit" | undefined;

/** A flow's headline: its application's own measure, with how good it is. */
export function flowHeadline(f: TrafficFlowResult): { text: string; tone: Tone } {
  if (f.error && !f.transactions && f.mos == null && !f.frames_sent && !f.answered) return { text: "did not run", tone: "crit" };
  switch (f.app) {
    case "voip": {
      const m = f.min_mos ?? f.mos;
      if (m == null) return { text: "—", tone: undefined };
      return { text: `MOS ${(f.mos ?? m).toFixed(2)}${f.min_mos != null && f.count > 1 ? `, worst call ${f.min_mos.toFixed(2)}` : ""}`, tone: m >= 4 ? "good" : m >= 3.6 ? "warn" : "crit" };
    }
    case "video": {
      const bad = (f.frames_lost_pct ?? 0) + (f.frames_late_pct ?? 0);
      return { text: `${(f.frames_lost_pct ?? 0).toFixed(1)}% frames lost, ${(f.frames_late_pct ?? 0).toFixed(1)}% late`, tone: bad < 1 ? "good" : bad < 5 ? "warn" : "crit" };
    }
    case "interactive":
    case "dns": {
      if (f.p95_ms == null) return { text: "no answers", tone: "crit" };
      const unanswered = (f.requests ?? 0) - (f.answered ?? 0);
      const limit = f.app === "dns" ? 100 : 150;
      return {
        text: `p95 ${f.p95_ms.toFixed(0)} ms${unanswered > 0 ? `, ${unanswered} unanswered` : ""}`,
        tone: unanswered > 0 ? "warn" : f.p95_ms <= limit ? "good" : f.p95_ms <= limit * 3 ? "warn" : "crit",
      };
    }
    case "web":
      if (f.p95_ms == null) return { text: "no pages loaded", tone: "crit" };
      return { text: `p95 page ${(f.p95_ms / 1000).toFixed(1)} s${f.failed ? `, ${f.failed} failed` : ""}`, tone: f.failed ? "warn" : f.p95_ms <= 3000 ? "good" : f.p95_ms <= 8000 ? "warn" : "crit" };
    case "stream":
      return { text: f.stalls ? `${f.stalls} stalls (${((f.stalled_ms ?? 0) / 1000).toFixed(1)} s)` : "no stalls", tone: f.stalls ? "crit" : "good" };
    case "bulk":
      return { text: kbps(f.goodput_kbps ?? 0), tone: undefined };
  }
}

function dirCell(d?: DirStats) {
  if (!d) return <span className="muted">—</span>;
  return (
    <span style={{ fontSize: 12.5 }}>
      {d.loss_pct.toFixed(d.loss_pct < 1 ? 2 : 1)}% loss · {d.jitter_ms.toFixed(1)} ms jitter
      {d.dscp_kept === false ? <> · <Pill tone="crit">DSCP {d.dscp_seen} on arrival</Pill></> : null}
    </span>
  );
}

/** A traffic run's result, flow by flow. */
export function TrafficResult({ result }: { result: TrafficResultData }) {
  return (
    <Table head={<><th>Flow</th><th>Result</th><th>Client → responder</th><th>Responder → client</th><th className="num">DSCP</th></>}>
      {result.flows.map((f) => {
        const h = flowHeadline(f);
        const kept = [f.up, f.down].filter((d): d is DirStats => !!d && d.dscp_kept != null);
        return (
          <tr key={f.id}>
            <td>
              <strong>{f.id}</strong> <span className="muted">{APPS[f.app]?.label ?? f.app} × {f.count}</span>
              {f.error ? <div className="err" style={{ fontSize: 12 }}>{f.error}</div> : null}
            </td>
            <td><Pill tone={h.tone}>{h.text}</Pill></td>
            <td>{dirCell(f.up)}</td>
            <td>{dirCell(f.down)}</td>
            <td className="num">
              {f.dscp}
              {kept.length ? (kept.every((d) => d.dscp_kept) ? <div className="muted" style={{ fontSize: 11 }}>kept</div> : null) : null}
            </td>
          </tr>
        );
      })}
    </Table>
  );
}
